import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import useTheme from '@mui/material/styles/useTheme';

import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import {
  StyledButton,
  StyledLoadingButton,
} from '~/components/ResponsiveDialog';

import { PrimaryColorTag } from '..';

interface FooterPrimaryActionProps {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  color?: PrimaryColorTag;
  dataTestId?: string;
}

interface SideDrawerFooterProps {
  onCancel?: () => void;
  cancelTestId?: string;
  isMobileView?: boolean;
  isCancelButtonPrimary?: boolean;
  footerPrimaryAction?: FooterPrimaryActionProps;
}

const SideDrawerFooter = ({
  onCancel = () => {},
  cancelTestId = 'cancel-button',
  footerPrimaryAction = undefined,
  isMobileView = false,
  isCancelButtonPrimary = false,
}: SideDrawerFooterProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'actions' });
  const theme = useTheme();
  const { label, onClick, loading, disabled, color, dataTestId } =
    footerPrimaryAction ?? {};

  const CancelButtonComponent = isMobileView ? StyledButton : Button;
  const PrimaryActionButtonComponent = isMobileView
    ? StyledLoadingButton
    : LoadingButtonWrapper;

  const cancelLabel = footerPrimaryAction ? t('cancel') : t('close');
  const primaryActionColor = color ?? 'primary';
  const footerActionTestId = dataTestId ?? 'footer-action-button';

  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      spacing={2}
      sx={{
        px: 4,
        py: 2,
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.grey[100],
        ...(isMobileView &&
          isCancelButtonPrimary && {
            pb: 4,
          }),
      }}
      className="ds-side-drawer-footer"
    >
      <CancelButtonComponent
        variant={isCancelButtonPrimary ? 'contained' : 'outlined'}
        onClick={onCancel}
        disabled={!onCancel}
        data-testid={cancelTestId}
      >
        {cancelLabel}
      </CancelButtonComponent>

      {footerPrimaryAction && (
        <PrimaryActionButtonComponent
          variant="contained"
          onClick={onClick}
          loading={loading}
          disabled={disabled}
          color={primaryActionColor}
          data-testid={footerActionTestId}
          sx={{
            '& .MuiLoadingButton-loadingIndicator': {
              color: theme.palette.grey.A200,
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.grey[300],
            },
          }}
        >
          {label}
        </PrimaryActionButtonComponent>
      )}
    </Stack>
  );
};

export default SideDrawerFooter;
